import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Trophy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Modal, Field, TextInput, Toggle, PrimaryButton } from "@/components/admin/ui";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const labelFromKey = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
};
const blankAgencies = () => [1, 2, 3].map((p) => ({ position: p, name: "", agency_id: "", image_url: "" }));

export default function ManageRankings() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [monthKey, setMonthKey] = useState("");
  const [published, setPublished] = useState(true);
  const [agencies, setAgencies] = useState(blankAgencies());

  const { data: items } = useQuery({ queryKey: ["admin-rankings"], queryFn: async () => (await api.get("/admin/rankings")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-rankings"] });
    qc.invalidateQueries({ queryKey: ["ranking-months"] });
    qc.invalidateQueries({ queryKey: ["ranking-latest"] });
    qc.invalidateQueries({ queryKey: ["ranking"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { month_key: monthKey, month_label: labelFromKey(monthKey), published, agencies };
      return editing ? api.put(`/admin/rankings/${editing}`, { ...payload, id: editing }) : api.post("/admin/rankings", payload);
    },
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Ranking updated" : "Ranking created"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/rankings/${id}`),
    onSuccess: () => { invalidate(); toast.success("Ranking deleted"); },
  });

  const openAdd = () => {
    setEditing(null); setMonthKey(new Date().toISOString().slice(0, 7)); setPublished(true); setAgencies(blankAgencies()); setModal(true);
  };
  const openEdit = (r) => {
    setEditing(r.id); setMonthKey(r.month_key); setPublished(r.published);
    const filled = blankAgencies().map((b) => r.agencies?.find((a) => a.position === b.position) || b);
    setAgencies(filled); setModal(true);
  };
  const updateAgency = (pos, key, val) => setAgencies((prev) => prev.map((a) => (a.position === pos ? { ...a, [key]: val } : a)));

  const togglePublish = (r) => {
    api.put(`/admin/rankings/${r.id}`, { ...r, published: !r.published }).then(() => {
      invalidate(); toast.success(r.published ? "Unpublished" : "Published");
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Top Agency Ranking</h1>
          <p className="mt-1 text-slate-400">Set the monthly Top 3 agencies. Only published months are visible to users.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-ranking-btn"><Plus className="h-5 w-5" /> New Month</PrimaryButton>
      </div>

      <div className="mt-8 space-y-3">
        {(items || []).map((r) => (
          <div key={r.id} className="card-surface rounded-xl p-4" data-testid={`admin-ranking-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6 text-gold" />
                <div>
                  <p className="font-bold text-slate-100">{r.month_label}</p>
                  <span className={`text-xs font-semibold ${r.published ? "text-whatsapp" : "text-slate-500"}`}>{r.published ? "Published" : "Draft"}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => togglePublish(r)} className="rounded-lg p-2 text-gold hover:bg-gold/10" title="Publish/Unpublish" data-testid={`publish-ranking-${r.id}`}>
                  {r.published ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
                <button onClick={() => openEdit(r)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-ranking-${r.id}`}><Pencil className="h-5 w-5" /></button>
                <button onClick={() => { if (window.confirm("Delete this month's ranking?")) del.mutate(r.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-ranking-${r.id}`}><Trash2 className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              {[...(r.agencies || [])].sort((a, b) => a.position - b.position).map((a) => (
                <span key={a.position} className="inline-flex items-center gap-1.5 rounded-full bg-obsidian px-3 py-1 text-xs text-slate-300">
                  <span className="font-bold text-gold">#{a.position}</span> {a.name || "—"}
                </span>
              ))}
            </div>
          </div>
        ))}
        {items?.length === 0 && <p className="py-12 text-center text-slate-500">No rankings yet. Create one for this month.</p>}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Ranking" : "New Monthly Ranking"} testid="ranking-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-5">
          <div className="grid grid-cols-2 items-center gap-3">
            <Field label="Month">
              <TextInput type="month" value={monthKey} onChange={(e) => setMonthKey(e.target.value)} required disabled={!!editing} testid="ranking-month-input" />
            </Field>
            <div className="pt-6">
              <Toggle checked={published} onChange={setPublished} label={published ? "Published" : "Draft"} testid="ranking-publish-toggle" />
            </div>
          </div>
          <p className="text-sm text-gold">{labelFromKey(monthKey)}</p>

          {agencies.map((a) => (
            <div key={a.position} className="rounded-xl border border-gold/15 bg-obsidian p-4" data-testid={`ranking-agency-${a.position}`}>
              <p className="mb-3 font-display text-lg gold-text">#{a.position} Agency</p>
              <div className="space-y-3">
                <ImageUpload value={a.image_url} onChange={(url) => updateAgency(a.position, "image_url", url)} label="Agency Picture" testid={`ranking-image-${a.position}`} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Agency Name">
                    <TextInput value={a.name} onChange={(e) => updateAgency(a.position, "name", e.target.value)} testid={`ranking-name-${a.position}`} />
                  </Field>
                  <Field label="Agency ID">
                    <TextInput value={a.agency_id} onChange={(e) => updateAgency(a.position, "agency_id", e.target.value)} testid={`ranking-id-${a.position}`} />
                  </Field>
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-ranking-btn">{save.isPending ? "Saving..." : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
